import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { loanOfficerOptions, priceRangeOptions, timelineOptions } from '../data/portal';
interface FormState {
  first: string;
  last: string;
  email: string;
  phone: string;
  consent: boolean;
  timeline: string;
  price: string;
  address: string;
  officer: string;
  notes: string;
}
type Errors = Partial<Record<keyof FormState, string>>;
const initial: FormState = {
  first: '',
  last: '',
  email: '',
  phone: '',
  consent: false,
  timeline: timelineOptions[1],
  price: priceRangeOptions[2],
  address: '',
  officer: 'Alex Moreno',
  notes: ''
};
const steps = [{
  title: 'Your loan officer reaches out',
  text: 'Usually within one business day, by the phone or email you provide.'
}, {
  title: 'Your buyer gets pre-approved',
  text: 'Once they apply, the file appears in My Files automatically.'
}, {
  title: 'You follow along here',
  text: 'Track every milestone, or just ask in the chat on Home.'
}];
const inputCls = (err?: string) => `mt-2 block w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 ${err ? 'border-red-400 focus:ring-red-100' : 'border-slate-300 focus:border-accent focus:ring-accent/15'}`;
export function ReferBuyer() {
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const set = <K extends keyof FormState,>(key: K, value: FormState[K]) => setForm(f => ({
    ...f,
    [key]: value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!form.first.trim()) next.first = 'Required';
    if (!form.last.trim()) next.last = 'Required';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.consent) next.consent = 'Please confirm the buyer agreed to be contacted.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 900);
  };
  return <div className="mx-auto max-w-[1200px]" data-id="element-310" data-mp="1mifm87oenezj">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-id="element-311" data-mp="1rjd2geputyye">Refer a Buyer</h1>
      <p className="mt-1 text-sm text-slate-500" data-id="element-312" data-mp="1vkosglcn4m74">Send a buyer straight to a loan officer. It takes about a minute.</p>

      <div className="mt-8 grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-313" data-mp="yuxziqjzz73f">
        {status === 'sent' ? <section className="animate-fade-up flex flex-col items-center rounded-2xl border border-slate-200 bg-white px-8 py-16 text-center" role="status" data-id="element-314" data-mp="13u0v7ke380yq">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-soft text-accent" data-id="element-315" data-mp="1cmckv41dnkdn">
              <CheckCircle2Icon className="h-7 w-7" data-id="element-316" data-mp="g4q28p2zbav5" />
            </span>
            <h2 className="mt-5 text-xl font-semibold text-slate-900" data-id="element-317" data-mp="k5i9jcsaj5os">Referral sent</h2>
            <p className="mt-2 max-w-sm text-sm text-slate-600" data-id="element-318" data-mp="k35ogqr8kf7i">
              {form.officer} has {form.first} {form.last}’s details and will reach out within one business day.
              We’ll notify you when the file opens.
            </p>
            <button onClick={() => {
          setForm(initial);
          setStatus('idle');
        }} className="mt-8 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent hover:-translate-y-px hover:shadow-sm active:translate-y-0" data-id="element-319" data-mp="3k9mn17zal1j">
              Refer another buyer
            </button>
          </section> : <form onSubmit={submit} noValidate className="rounded-2xl border border-slate-200 bg-white p-8" data-id="element-320" data-mp="372axzkcnmdwg">
            <fieldset data-id="element-321" data-mp="323zpbcy3i9ft">
              <legend className="text-base font-semibold text-slate-900" data-id="element-322" data-mp="323ghip25t1fi">Buyer</legend>
              <div className="mt-4 grid grid-cols-2 gap-5" data-id="element-323" data-mp="1614onn3uu4cj">
                {([['first', 'First name', 'text', 'given-name'], ['last', 'Last name', 'text', 'family-name'], ['email', 'Email', 'email', 'off'], ['phone', 'Phone', 'tel', 'off']] as const).map(([key, label, type, ac]) => <div key={key} data-id="element-324" data-mp="nbexd9zaw0pd">
                    <label htmlFor={key} className="text-sm font-medium text-slate-700" data-id="element-325" data-mp="ransoj9zo6n1">
                      {label}
                      {key !== 'phone' && <span className="text-slate-400" data-id="element-326" data-mp="dcfuy954p1at"> *</span>}
                    </label>
                    <input id={key} type={type} autoComplete={ac} value={form[key]} onChange={e => set(key, e.target.value)} aria-invalid={!!errors[key]} className={inputCls(errors[key])} data-id="element-327" data-mp="ib0ndme52gop" />
                    {errors[key] && <p className="mt-1.5 text-xs text-red-600" data-id="element-328" data-mp="3s7zixg885oof">{errors[key]}</p>}
                  </div>)}
              </div>
              <label className="mt-5 flex cursor-pointer items-start gap-3" data-id="element-329" data-mp="as15jl6bsx7g">
                <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0E7C86]" aria-invalid={!!errors.consent} data-id="element-330" data-mp="5z816yerrqvw" />
                <span className="text-sm text-slate-700" data-id="element-331" data-mp="tosti7wtc0hu">
                  Buyer agreed to be contacted <span className="text-slate-400" data-id="element-332" data-mp="or0t0pi3r621">*</span>
                </span>
              </label>
              {errors.consent && <p className="ml-7 mt-1.5 text-xs text-red-600" data-id="element-333" data-mp="2vj1ojz4dzsrs">{errors.consent}</p>}
            </fieldset>

            <fieldset className="mt-8 border-t border-slate-100 pt-8" data-id="element-334" data-mp="2qk764yac0687">
              <legend className="sr-only" data-id="element-335" data-mp="3g7krmpeitwxt">Their search</legend>
              <h2 className="text-base font-semibold text-slate-900" aria-hidden="true" data-id="element-336" data-mp="3k6ti9le9lep5">
                Their search
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-5" data-id="element-337" data-mp="3p5d47lsuyqhi">
                <div data-id="element-338" data-mp="3u615r1epqppz">
                  <label htmlFor="timeline" className="text-sm font-medium text-slate-700" data-id="element-339" data-mp="3u6kcdlywxrop">
                    Purchase timeline
                  </label>
                  <select id="timeline" value={form.timeline} onChange={e => set('timeline', e.target.value)} className={inputCls()} data-id="element-340" data-mp="70tg9fkpwyu2">
                    {timelineOptions.map(o => <option key={o} data-id="element-341" data-mp="gqv200plm55t">{o}</option>)}
                  </select>
                </div>
                <div data-id="element-342" data-mp="lqhf8a5fflem">
                  <label htmlFor="price" className="text-sm font-medium text-slate-700" data-id="element-343" data-mp="lr0qj80knbjo">
                    Price range
                  </label>
                  <select id="price" value={form.price} onChange={e => set('price', e.target.value)} className={inputCls()} data-id="element-344" data-mp="qmoa97wabchs">
                    {priceRangeOptions.map(o => <option key={o} data-id="element-345" data-mp="266p1qzv9kk2m">{o}</option>)}
                  </select>
                </div>
                <div className="col-span-2" data-id="element-346" data-mp="21aqe5ehrf639">
                  <label htmlFor="address" className="text-sm font-medium text-slate-700" data-id="element-347" data-mp="1wavp7ghjoqku">
                    Property address <span className="font-normal text-slate-400" data-id="element-348" data-mp="2tw9lhkz8rlcy">(optional)</span>
                  </label>
                  <input id="address" value={form.address} onChange={e => set('address', e.target.value)} placeholder="If they’re already under contract" className={inputCls()} data-id="element-349" data-mp="2oyzw25bur9p4" />
                </div>
              </div>
            </fieldset>

            <fieldset className="mt-8 border-t border-slate-100 pt-8" data-id="element-350" data-mp="37wu2cbllj73t">
              <legend className="sr-only" data-id="element-351" data-mp="2g9spklrhz8su">Routing</legend>
              <h2 className="text-base font-semibold text-slate-900" aria-hidden="true" data-id="element-352" data-mp="2g8zhnhgsyd94">
                Routing
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-5" data-id="element-353" data-mp="2l2c3jju3k5y6">
                <div data-id="element-354" data-mp="27adypl7f4w7t">
                  <label htmlFor="officer" className="text-sm font-medium text-slate-700" data-id="element-355" data-mp="27axg90t5pilb">
                    Loan officer
                  </label>
                  <select id="officer" value={form.officer} onChange={e => set('officer', e.target.value)} className={inputCls()} data-id="element-356" data-mp="2zx8fo8511dgj">
                    {loanOfficerOptions.map(o => <option key={o} data-id="element-357" data-mp="2pzuax5sgud42">{o}</option>)}
                  </select>
                </div>
                <div className="col-span-2" data-id="element-358" data-mp="2uxvtzqpw6cfj">
                  <label htmlFor="notes" className="text-sm font-medium text-slate-700" data-id="element-359" data-mp="2v0ru9lp7pd75">
                    Notes
                  </label>
                  <textarea id="notes" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything that helps — best time to call, first-time buyer, etc." className={inputCls()} data-id="element-360" data-mp="1jk1mhy554dbf" />
                </div>
              </div>
            </fieldset>

            <div className="mt-8 flex items-center justify-end gap-4 border-t border-slate-100 pt-6" data-id="element-361" data-mp="1g2nmzxsms0br">
              <button type="submit" disabled={status === 'sending'} className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-brand-dark focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/30 disabled:opacity-70 hover:-translate-y-px hover:shadow-md active:translate-y-0 disabled:hover:translate-y-0 disabled:hover:shadow-none" data-id="element-362" data-mp="sctajhc84ds1">
                {status === 'sending' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-363" data-mp="329rbjjxt4t1w" />}
                {status === 'sending' ? 'Sending…' : 'Send referral'}
              </button>
            </div>
          </form>}

        <aside className="rounded-2xl border border-slate-200 bg-white p-6" data-id="element-364" data-mp="2sajxsvn5uwe9">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-365" data-mp="2o9zzhoqupmqj">What happens next</h2>
          <ol className="mt-5 space-y-6" data-id="element-366" data-mp="2jan58ixnhddx">
            {steps.map((s, i) => <li key={s.title} className="flex gap-4" data-id="element-367" data-mp="2jcq4zuolukvu" data-mpi={i}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand" data-id="element-368" data-mp="2eivmuvonedyv">
                  {i + 1}
                </span>
                <div data-id="element-369" data-mp="24milrcqrwxu3">
                  <p className="text-sm font-semibold text-slate-900" data-id="element-370" data-mp="24nbsidpqfnc8">{s.title}</p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-371" data-mp="20j42md7jg1cf">{s.text}</p>
                </div>
              </li>)}
          </ol>
        </aside>
      </div>
    </div>;
}